import { type ElementSize } from "../hooks/use-element-size.ts"

// A 15- and a 30-minute meeting render at the exact same 28px: FullCalendar's
// `eventMinHeight={28}` (Calendar.tsx) clamps anything shorter, and 30 minutes
// is calendar.less' own `@slot-height` for a 30-minute slot. A duration cutoff
// therefore can't tell those two apart, and one that tried (`diffInMinutes >=
// 30`) shipped a real bug: it put the record+person lines on a 30-minute card
// that has no more pixels than a 15-minute one, clipping into them in a real
// browser (invisible to jsdom, which is why it shipped, and why the "bigger
// text past 60 minutes" tier had the same problem one size up — a 60-minute
// box a real browser measures at 56px cannot fit the bigger-text 3-line
// stack, which needs closer to 64). These cutoffs key off the rendered box
// instead, calibrated against this component's own line heights (title/detail
// text at `leading-[1.2]`, plus the small inter-line gap) with a safety
// margin, so two boxes of the same size always carry the same content
// regardless of why they ended up that size.
const HEIGHT_FOR_DETAILS = 48
const HEIGHT_FOR_EXTRA_ROOM = 70

// A narrow overlapping column still has to read as one clean title; the time
// and the record/person lines are dropped rather than rendered as unreadable
// slivers or left to overflow the card (CalendarTimeAndType's time text is
// deliberately `shrink-0` — it either shows in full or not at all).
const MIN_WIDTH_FOR_TIME = 64
const MIN_WIDTH_FOR_DETAILS = 90

export interface EventCardTier {
  showTime: boolean
  showDetails: boolean
  /** More room than the shown lines need — size text up and space lines out. */
  hasExtraRoom: boolean
}

export function pickEventCardTier({ width, height }: ElementSize): EventCardTier {
  return {
    showTime: width >= MIN_WIDTH_FOR_TIME,
    showDetails: width >= MIN_WIDTH_FOR_DETAILS && height >= HEIGHT_FOR_DETAILS,
    hasExtraRoom: height >= HEIGHT_FOR_EXTRA_ROOM
  }
}

// calendar.less' `@slot-height` (28px per 30-minute slot) and Calendar.tsx's
// `eventMinHeight={28}` floor, mirrored here for the one case a real
// measurement isn't available yet: initial mount, and jsdom in tests, which
// never runs layout at all (see `useElementSize`).
const SLOT_HEIGHT_PX = 28
const SLOT_MINUTES = 30
const MIN_EVENT_HEIGHT_PX = 28

export function estimateEventHeightPx(durationMinutes: number): number {
  if (!Number.isFinite(durationMinutes)) return MIN_EVENT_HEIGHT_PX

  return Math.max(
    MIN_EVENT_HEIGHT_PX,
    (durationMinutes / SLOT_MINUTES) * SLOT_HEIGHT_PX
  )
}
